/**
 * EvaluationsPanel — one olive bar per eval group, showing THIS model's
 * score and nothing else. Comparisons (baseline / competitor / SOTA) belong
 * on the group page, where there is room to say what is being compared;
 * here they crowd the number the page is about. Group names link through.
 */

import { Link } from 'react-router-dom'

import type { EvalGroupSummary } from '@/api/generated/model'
import { formatScore } from '@/components/evals/format'

interface EvaluationsPanelProps {
  groups: EvalGroupSummary[]
}

function EvalBar({ group }: { group: EvalGroupSummary }): React.JSX.Element {
  const score = group.score
  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <Link
          to={`/evals/groups/${group.slug}`}
          className="truncate text-sm hover:underline"
        >
          {group.display_name}
        </Link>
        <span className="shrink-0 font-mono text-xs tabular-nums">
          {formatScore(score, group.score_format)}
        </span>
      </div>
      <div
        className="relative mt-2 h-1.5 overflow-hidden rounded-sm bg-border/60"
        role="img"
        aria-label={`${group.display_name}: ${formatScore(score, group.score_format)}`}
      >
        {score !== null && (
          // Fraction of full scale, so this reads as a meter only for a rate.
          // One row per group and no shared unit between them means there is no
          // range to scale against here (unlike the group page, which owns all
          // of one group's scores) — an unbounded metric just pins the meter
          // full, and the printed value beside it carries the real number.
          <span
            className="absolute inset-y-0 left-0 bg-secondary"
            style={{ width: `${(Math.min(score, 1) * 100).toFixed(1)}%` }}
          />
        )}
      </div>
    </div>
  )
}

export function EvaluationsPanel({
  groups,
}: EvaluationsPanelProps): React.JSX.Element {
  if (groups.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No eval scores for this model yet. Runs pushed via the public evals
        API appear here once one names this model.
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <EvalBar key={group.id} group={group} />
      ))}
    </div>
  )
}
